import React from 'react';
import { AppState, Booking as BookingModel } from 'acscp/store';
import { QuestionMarkCircle } from 'acscp/components/common/questionMarkCircle';
import style from './booking.module.scss';
import { FlightPlan } from 'acscp/components/legs/flightPlan/flightPlan';
import { SingleLineFlightPlanTrack } from 'acscp/components/legs/flightPlan/singleLineFlightPlanTrack';
import { TextOnlyFlightPlan } from 'acscp/components/legs/flightPlan/textOnlyFlightPlan';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { getStringsForBooking } from 'acscp/components/common/helpers/bookingStatus';
import { BookingButton } from 'acscp/components/bookings/bookingButton';
import { FormattedDate } from 'acscp/components/common/formattedDate';
import { Tooltip } from '../common/tooltip';
import { userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { connect } from 'react-redux';

const TOOLTIP_OFFSET_TOP = -10;
const TOOLTIP_OFFSET_LEFT = -40;

interface BookingOwnProps {
    booking: BookingModel;
    read: boolean;
    canPotentiallyRequestFlights: boolean;
}

interface BookingStateProps {
    isLoggedIn: boolean;
}

export class BookingPresentational extends React.Component<BookingOwnProps & BookingStateProps & InjectedIntlProps, {}> {
    render(): JSX.Element | null {
        const booking = this.props.booking;

        if (booking.legs.length === 0) {
            return null;
        }

        const bookingIsExpired = booking.status === 'Expired' || booking.status === 'EnquiryExpired';

        const classes = classNames(style.Booking, {
            [style.Expired]: bookingIsExpired
        });

        const { intl } = this.props;
        const bookingStrings = getStringsForBooking(intl.formatMessage, booking);
        const tooltipId = `tooltip-${booking.enquiryId}`;

        const departureDate = booking.legs[0].departureDate;
        const arrivalDate = booking.legs[booking.legs.length - 1].arrivalDate;

        const showOnlyOneDate = departureDate.diff(arrivalDate, true) === 0;

        return (
            <div className={classes}>
                {!bookingIsExpired &&
                    <div className={style.TopBar}>
                        {this.props.isLoggedIn && !this.props.read &&
                            <span className={style.Unread}>
                                <span className={style.UnreadCircle} />
                                <FormattedMessage id={'components.bookings.booking.unread'} defaultMessage={'Unread'} />
                            </span>
                        }
                    </div>
                }
                <div className={style.FlightInfoBlock}>
                    <div className={style.FlightInfoLeft}>
                        <div className={classNames(style.FlightInfoItem, style.Dates)}>
                            <span className={style.Date}>
                                <FormattedDate date={departureDate} />
                            </span>
                            {!showOnlyOneDate &&
                                <span className={classNames(style.Date, style.ArrivalDate)}>
                                    <FormattedDate date={arrivalDate} />
                                </span>
                            }
                        </div>
                        {!booking.ownedByCurrentUser && booking.ownerOfBookingName && (
                            <div className={style.FlightInfoItem}>
                                <span className={style.FlightInfoLabel}>
                                    <FormattedMessage
                                        id={'components.bookings.booking.accountHolder'}
                                        defaultMessage={'ACCOUNT HOLDER'}
                                    />
                                </span>
                                <span className={style.FlightInfoValue}>
                                    {booking.ownerOfBookingName}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className={style.FlightInfoRight}>
                        <div className={style.FlightInfoItem}>
                            <span className={classNames(style.FlightInfoLabel, style.ShortRefLabel)}>
                                <FormattedMessage
                                    id={'components.bookings.booking.ref'}
                                    defaultMessage={'REF'}
                                />
                            </span>
                            <span className={classNames(style.FlightInfoLabel, style.LongRefLabel)}>
                                <FormattedMessage
                                    id={'components.bookings.booking.reference'}
                                    defaultMessage={'REFERENCE'}
                                />
                            </span>
                            <span className={style.FlightInfoValue}>
                                {booking.enquiryId}
                            </span>
                        </div>
                        {!booking.ownedByCurrentUser && booking.sharedByName && (
                            <div className={style.FlightInfoItem}>
                                <span className={style.FlightInfoLabel}>
                                    <FormattedMessage
                                        id={'components.bookings.booking.sharedBy'}
                                        defaultMessage={'SHARED BY'}
                                    />
                                </span>
                                <span className={style.FlightInfoValue}>
                                    {booking.sharedByName}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className={style.TrackBorders}>
                        <FlightPlan
                            desktopFlightPlan={
                                <SingleLineFlightPlanTrack
                                    legs={booking.legs}
                                    withDurations={false}
                                    withBackground={true}
                                />
                            }
                            mobileFlightPlan={
                                <TextOnlyFlightPlan
                                    legs={booking.legs}
                                    withLogo={false}
                                />
                            }
                        />
                    </div>
                </div>

                { // If the booking was auto-published, show only a summary of the booking (so hide these details)
                    !booking.autoPublishedToCustomerPortal &&
                    <div className={style.FlightStatusBlock}>
                        <div className={style.Status}>
                        { booking.status !== 'Flown'
                            ? <div>
                                <div className={style.BookingStatus}>
                                    {bookingStrings.status}
                                </div>
                                <Tooltip
                                    className={style.Question}
                                    tooltipId={tooltipId}
                                    tooltipContents={bookingStrings.whatsThis}
                                    offsetTop={TOOLTIP_OFFSET_TOP}
                                    offsetLeft={TOOLTIP_OFFSET_LEFT}
                                >
                                    <FormattedMessage
                                        id={'components.bookings.booking.whatIsThis'}
                                        defaultMessage={'What is this'}
                                    />
                                    <QuestionMarkCircle />
                                </Tooltip>
                            </div>
                            : <div className={style.BookingAircraftType}>
                                AIRCRAFT: {booking.aircraftType}
                            </div>
                        }
                        </div>
                        <BookingButton booking={booking} canPotentiallyRequestFlights={this.props.canPotentiallyRequestFlights} />
                    </div>
                }
            </div>
        );
    }
}

function mapStateToProps(state: AppState, ownProps: {}): BookingStateProps {
    return {
        isLoggedIn: userIsLoggedIn(state.auth)
    };
}

const Booking = connect(mapStateToProps)(injectIntl(BookingPresentational));

export { Booking };
